import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { translations, contactInfo } from "../i18n/translations";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { lang, t } = useLanguage();
  const tx = translations.contact;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang }, { timeout: 25000 });
      toast({
        title: t(tx.form.successTitle),
        description: t(tx.form.successDesc)
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const fallbackMsg =
        lang === "es"
          ? "No pudimos enviar su mensaje. Por favor intente de nuevo o contáctenos por WhatsApp."
          : "We could not send your message. Please try again or contact us via WhatsApp.";
      toast({
        title: lang === "es" ? "Error al enviar" : "Failed to send",
        description: err?.response?.data?.detail || fallbackMsg,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="bg-[#F5F0E8] py-24 lg:py-36">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[1px] w-10 bg-[#9C8456]" />
            <span
              className="text-[#9C8456] text-[11px] tracking-[0.45em] uppercase"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {t(tx.eyebrow)}
            </span>
            <div className="h-[1px] w-10 bg-[#9C8456]" />
          </div>
          <h2
            className="text-[#2A2A28] text-[34px] sm:text-[48px] lg:text-[58px] leading-[1.08] max-w-3xl mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t(tx.title1)} <em className="italic">{t(tx.title2)}</em>
          </h2>
          <p
            className="text-[#6B6760] text-[15px] lg:text-[17px] leading-[1.7] mt-6 max-w-xl mx-auto"
            style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
          >
            {t(tx.intro)}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact details with both contacts */}
          <div className="lg:col-span-5">
            <div className="border border-[#E0DACE] p-8 lg:p-10 bg-[#FFFCF6] h-full">
              <p
                className="text-[#9C8456] text-[10px] tracking-[0.4em] uppercase mb-6"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(tx.listingTag)}
              </p>

              {/* Email block */}
              <div className="flex items-start gap-4 mb-7">
                <div className="flex-shrink-0 w-11 h-11 rounded-full border border-[#9C8456]/40 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#9C8456]" strokeWidth={1.4} />
                </div>
                <div>
                  <p
                    className="text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-2"
                    style={{ fontFamily: "'Jost', sans-serif" }}
                  >
                    {t(tx.emailLabel)}
                  </p>
                  {contactInfo.emails.map((email) => (
                    <a
                      key={email}
                      href={`mailto:${email}`}
                      className="block text-[#2A2A28] text-[15px] hover:text-[#9C8456] transition-colors mb-1"
                      style={{ fontFamily: "'Jost', sans-serif" }}
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>

              {/* Phone block */}
              <div className="flex items-start gap-4 mb-7">
                <div className="flex-shrink-0 w-11 h-11 rounded-full border border-[#9C8456]/40 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#9C8456]" strokeWidth={1.4} />
                </div>
                <div>
                  <p
                    className="text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-2"
                    style={{ fontFamily: "'Jost', sans-serif" }}
                  >
                    {t(tx.phoneLabel)}
                  </p>
                  {contactInfo.phones.map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone.replace(/\s/g, "")}`}
                      className="block text-[#2A2A28] text-[15px] hover:text-[#9C8456] transition-colors mb-1"
                      style={{ fontFamily: "'Jost', sans-serif" }}
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-full border border-[#9C8456]/40 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#9C8456]" strokeWidth={1.4} />
                </div>
                <div>
                  <p
                    className="text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-2"
                    style={{ fontFamily: "'Jost', sans-serif" }}
                  >
                    {t(tx.locationLabel)}
                  </p>
                  <p
                    className="text-[#2A2A28] text-[15px] leading-[1.6]"
                    style={{ fontFamily: "'Jost', sans-serif" }}
                  >
                    {t(tx.location)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-7">
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label
                  className="block text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-3"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  {t(tx.form.name)}
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-[#9C8456]/40 focus:border-[#2A2A28] outline-none py-3 text-[16px] text-[#2A2A28] transition-colors duration-300"
                  style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
                />
              </div>
              <div>
                <label
                  className="block text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-3"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  {t(tx.form.email)}
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-[#9C8456]/40 focus:border-[#2A2A28] outline-none py-3 text-[16px] text-[#2A2A28] transition-colors duration-300"
                  style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
                />
              </div>
            </div>
            <div>
              <label
                className="block text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-3"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(tx.form.phone)}
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="w-full bg-transparent border-b border-[#9C8456]/40 focus:border-[#2A2A28] outline-none py-3 text-[16px] text-[#2A2A28] transition-colors duration-300"
                style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
              />
            </div>
            <div>
              <label
                className="block text-[#9C8456] text-[10px] tracking-[0.35em] uppercase mb-3"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(tx.form.message)}
              </label>
              <textarea
                required
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                className="w-full bg-transparent border-b border-[#9C8456]/40 focus:border-[#2A2A28] outline-none py-3 text-[16px] text-[#2A2A28] resize-none transition-colors duration-300"
                style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center justify-center gap-3 px-10 py-4 text-[11px] tracking-[0.3em] uppercase bg-[#2A2A28] text-[#F5F0E8] hover:bg-[#9C8456] transition-colors duration-300 disabled:opacity-60"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {sending ? t(tx.form.sending) : t(tx.form.send)}
              {!sending && <Send className="w-3.5 h-3.5" strokeWidth={1.4} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
